import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Monitor, Moon, RefreshCcw, Sun } from 'lucide-react';
import { ThemeProvider as NextThemeProvider, useTheme } from 'next-themes';
import type * as React from 'react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import type { ProductKey } from '../../lib/app-registry';
import { PostHogProvider, webExceptionReporter } from '../../lib/posthog';
import { queryClient, trpc, trpcReactClient } from '../../lib/trpc';
import { removeWorkspaceScopedQueries } from '../../lib/workspace-query-cache';
import type { CommandDestination } from '../../types/command-palette';
import { Button } from '../../ui/button';
import { SidebarInset, SidebarProvider } from '../../ui/sidebar';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { SeatStatusGate } from '../billing/SeatStatusGate';
import { CommandPaletteProvider } from '../command-palette/CommandPaletteProvider';
import { ExceptionBoundary } from '../exception-boundary';
import { ToastProvider } from '../toast';

type AuthStateSubscriber = (cb: (isAuthed: boolean) => void) => () => void;

/**
 * Every portal installs its subscriber in `src/main.tsx`, before render. There is no default
 * provider on purpose: the previous Firebase Auth lazy import statically pulled its SDK (and
 * remotely hosted-code URLs) into every bundle, although no app reached that branch after the
 * Better Auth migration. ProtectedRoute/WorkspaceRedirector below only check the callback value
 * for truthiness, never read fields off a user object, so a boolean is all any provider must supply.
 */
let activeAuthStateSubscriber: AuthStateSubscriber = (cb) => {
  console.error(
    'AppShell: setAuthStateSubscriber() was never called — treating the session as signed out. Install one in the portal entry point before rendering.'
  );
  cb(false);
  return () => {};
};

export function setAuthStateSubscriber(subscriber: AuthStateSubscriber) {
  activeAuthStateSubscriber = subscriber;
}

// ==========================================
// 1. Error Boundary
// ==========================================
export interface ErrorBoundaryProps {
  children?: ReactNode;
}

export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <ExceptionBoundary
      reporter={webExceptionReporter}
      fallback={(error) => (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
          <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-lg text-center">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <h1 className="text-xl font-bold tracking-tight mb-2">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mb-6">
              An unexpected error occurred in the application.
            </p>
            <div className="mb-6 p-3 bg-muted rounded text-left text-[10px] font-mono overflow-auto max-h-32 border border-border/50 select-all">
              <div className="font-bold text-destructive mb-1 uppercase tracking-wider">
                Error Details (AI Context)
              </div>
              <div>
                {error?.name}: {error?.message}
              </div>
              <div className="mt-2 text-muted-foreground opacity-70">
                {error?.stack?.split('\n').slice(0, 3).join('\n')}
              </div>
            </div>
            <Button onClick={() => window.location.reload()} variant="outline" className="w-full">
              <RefreshCcw className="mr-2 h-4 w-4" />
              Reload Application
            </Button>
          </div>
        </div>
      )}
    >
      {children}
    </ExceptionBoundary>
  );
}

// ==========================================
// 2. Theme Switcher
// ==========================================
// Light/Dark/System pill toggle, meant to live in a sidebar header (near the logo) rather
// than a separate top header bar — see WorkspaceLayout below, which has no header of its own.
export function ThemeSwitcher({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;

  return (
    <Tabs value={theme ?? 'system'} onValueChange={setTheme} className={className}>
      <TabsList className="w-full">
        {options.map(({ value, label, icon: Icon }) => (
          <Tooltip key={value}>
            <TooltipTrigger asChild>
              <TabsTrigger value={value} className="flex-1 px-0">
                <Icon className="h-3.5 w-3.5" />
                <span className="sr-only">{label}</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        ))}
      </TabsList>
    </Tabs>
  );
}

// ==========================================
// 3. Protected Route Guard
// ==========================================
interface ProtectedRouteProps {
  children: React.ReactNode;
  loginPath?: string;
}

export function ProtectedRoute({ children, loginPath = '/login' }: ProtectedRouteProps) {
  const [isAuthed, setIsAuthed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return activeAuthStateSubscriber((authed) => {
      setIsAuthed(authed);
      setLoading(false);
    });
  }, []);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: !loading && isAuthed,
  });

  useEffect(() => {
    if (meQuery.data?.workspaces && meQuery.data.workspaces.length > 0) {
      const storedId = localStorage.getItem('workspaceId');
      const activeWorkspace =
        storedId && meQuery.data.workspaces.find((w) => w.id.toString() === storedId)
          ? meQuery.data.workspaces.find((w) => w.id.toString() === storedId)
          : meQuery.data.workspaces[0];

      if (activeWorkspace) {
        localStorage.setItem('workspaceId', activeWorkspace.id.toString());
        localStorage.setItem('organizationId', activeWorkspace.organizationId.toString());
      }
    }
  }, [meQuery.data]);

  if (loading || (isAuthed && meQuery.isLoading)) {
    return null;
  }

  if (!isAuthed) return <Navigate to={loginPath} replace />;

  return <>{children}</>;
}

// ==========================================
// 4. Workspace Redirector
// ==========================================
interface WorkspaceRedirectorProps {
  loginPath?: string;
  defaultPath?: string;
}

export function WorkspaceRedirector({
  loginPath = '/login',
  defaultPath = 'dashboard',
}: WorkspaceRedirectorProps) {
  const location = useLocation();
  const [isAuthed, setIsAuthed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return activeAuthStateSubscriber((authed) => {
      setIsAuthed(authed);
      setLoading(false);
    });
  }, []);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: !loading && isAuthed,
  });

  if (loading || (isAuthed && meQuery.isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Initializing workspace...</p>
        </div>
      </div>
    );
  }

  if (!isAuthed) return <Navigate to={loginPath} replace />;

  const workspaces = meQuery.data?.workspaces || [];
  if (workspaces.length === 0) {
    return <Navigate to={loginPath} replace />;
  }

  const storedId = localStorage.getItem('workspaceId');
  const activeWorkspace =
    storedId && workspaces.find((w) => w.id.toString() === storedId)
      ? workspaces.find((w) => w.id.toString() === storedId)!
      : workspaces[0];

  const activeId = activeWorkspace.id.toString();
  const activeOrgId = activeWorkspace.organizationId.toString();

  localStorage.setItem('workspaceId', activeId);
  localStorage.setItem('organizationId', activeOrgId);
  const subpath = defaultPath.replace(/^\//, '');
  // Preserve the incoming query string (e.g. Stripe's `?upgrade=success&session_id=...` return)
  // — a caller landing on this bare, workspace-agnostic route is relying on it surviving the hop
  // to the real org/workspace-scoped URL, and `<Navigate>` does not carry it over on its own.
  return (
    <Navigate
      to={`/org/${activeOrgId}/workspace/${activeId}/${subpath}${location.search}`}
      replace
    />
  );
}

// ==========================================
// 5. Workspace Layout Wrapper
// ==========================================
interface WorkspaceLayoutProps {
  SidebarComponent: React.ComponentType;
  /**
   * Supplies the Ctrl/Cmd+K palette's "Go to" list for this app. Called with the current route's
   * ids so destinations come back already workspace-scoped. Apps pass their `buildNavItems`
   * — the same definitions their sidebar renders — so the two can't drift apart.
   *
   * Omit to mount the layout without a palette.
   */
  buildDestinations?: (orgId: string, workspaceId: string) => CommandDestination[];
  /**
   * The product this portal gates seats for. When supplied, a viewer whose own seat is paused
   * (assigned, but over the plan's current cap) sees `SeatPausedNotice` here — the one choke
   * point every route already shares — instead of every individual content query 403ing
   * independently with no unified explanation. Omit for a portal with no seat gating.
   */
  productKey?: ProductKey;
}

export function WorkspaceLayout({
  SidebarComponent,
  buildDestinations,
  productKey,
}: WorkspaceLayoutProps) {
  const { workspaceId, orgId } = useParams<{ workspaceId: string; orgId: string }>();
  const activeQueryClient = useQueryClient();
  const [activeWsId, setActiveWsId] = useState<string | null>(null);
  const [trackedWorkspaceId, setTrackedWorkspaceId] = useState(workspaceId);

  // Computed above the early return below so hook order stays stable across the
  // unmount/remount cycle that a workspace switch triggers.
  const destinations = useMemo(
    () => (buildDestinations && orgId && workspaceId ? buildDestinations(orgId, workspaceId) : []),
    [buildDestinations, orgId, workspaceId]
  );

  useEffect(() => {
    if (workspaceId) {
      localStorage.setItem('workspaceId', workspaceId);
    }
    if (orgId) {
      localStorage.setItem('organizationId', orgId);
    }
  }, [workspaceId, orgId]);

  // Force a full unmount/remount of the workspace subtree when switching workspaces, so no
  // component state or query cache bleeds across workspaces. Step 1 (immediate unmount) happens
  // here during render via React's documented "adjust state while rendering" pattern; step 2
  // (remount next frame, once the unmount has committed) happens in the effect below.
  if (workspaceId !== trackedWorkspaceId) {
    setTrackedWorkspaceId(workspaceId);
    setActiveWsId(null);
  }

  useEffect(() => {
    if (activeWsId !== null || !workspaceId) return;

    // Clear queryClient cache for workspace data to prevent cross-workspace data bleed
    removeWorkspaceScopedQueries(activeQueryClient);

    // Defer mounting the next workspace until the next animation frame (guarantees unmount transaction completes)
    const raf = requestAnimationFrame(() => {
      setActiveWsId(workspaceId);
    });
    return () => cancelAnimationFrame(raf);
  }, [activeWsId, workspaceId, activeQueryClient]);

  if (!activeWsId || activeWsId !== workspaceId) {
    return null; // Empty frame to guarantee full destruction before next mount
  }

  return (
    // `h-svh` (not the primitive's `min-h-svh`) plus `overflow-hidden` pins the
    // shell to the viewport, which is what gives SidebarInset below a *definite*
    // height to scroll against. Without it the wrapper grows past the viewport
    // and `#root { overflow: hidden }` in each portal's index.css silently clips
    // the overflow — no scrollbar anywhere. That is why ordinary document pages
    // (Settings, Profile, a contact detail) could not scroll, while pages that
    // happened to set their own `h-full overflow-y-auto` could.
    <SeatStatusGate productKey={productKey}>
      <SidebarProvider className="h-svh overflow-hidden">
        <CommandPaletteProvider destinations={destinations}>
          <SidebarComponent />
          {/* The app's one scroll region. `min-h-0` is load-bearing: a flex child
              defaults to min-height:auto and would refuse to shrink below its
              content, so it would never overflow and never scroll. Pages that
              manage their own scrolling still work — they size to this box rather
              than exceeding it, so no second scrollbar appears. */}
          <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <Outlet />
          </SidebarInset>
        </CommandPaletteProvider>
      </SidebarProvider>
    </SeatStatusGate>
  );
}

// ==========================================
// 6. Unified App Shell Provider
// ==========================================
interface AppShellProviderProps {
  children: React.ReactNode;
}

export function AppShellProvider({ children }: AppShellProviderProps) {
  return (
    <PostHogProvider>
      <ErrorBoundary>
        <trpc.Provider client={trpcReactClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <NextThemeProvider
              attribute="class"
              defaultTheme="system"
              enableSystem
              disableTransitionOnChange
            >
              <ToastProvider>{children}</ToastProvider>
            </NextThemeProvider>
          </QueryClientProvider>
        </trpc.Provider>
      </ErrorBoundary>
    </PostHogProvider>
  );
}
